import { liteClient } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';

const mapFacetToRefinement = {
	country: 'tax_country.name',
	'cbg-theme': 'tax_cbg-theme.name',
	'cbg-month': 'tax_cbg-month.name',
	'cbg-year': 'tax_cbg-year.name',
	collection: 'tax_collection.name',
	'research-type': 'tax_research-type.name',
	'document-type': 'tax_document-type.name',
	'event-type': 'tax_event-type.name',
};

export function getVisiblePages(currentRefinement, nbPages, totalPages) {
	const pages = [];
	const leftEllipsis = '...';
	const rightEllipsis = '...';

	// If there are fewer pages than the max visible pages, show all pages
	if (nbPages <= totalPages) {
		for (let i = 0; i < nbPages; i++) {
			pages.push(i);
		}
		return pages;
	}

	// Always show the first page
	pages.push(0);

	// Calculate
	let startPage = Math.max(currentRefinement - Math.floor(totalPages / 2), 1);
	let endPage = Math.min(currentRefinement + Math.floor(totalPages / 2), nbPages - 1);

	if (startPage === 1) endPage = Math.min(totalPages - 1, nbPages - 1);
	if (endPage === nbPages - 1) startPage = Math.max(nbPages - totalPages, 1);

	for (let i = startPage; i <= endPage; i++) {
		pages.push(i);
	}

	if (pages[1] > 1) pages.splice(1, 0, leftEllipsis);
	if (pages[pages.length - 2] < nbPages - 2) pages.push(rightEllipsis);

	// Always show the last page
	if (pages[pages.length - 1] !== nbPages - 1) pages.push(nbPages - 1);

	return pages;
}

function createConditionalSearchClient(client, minQueryLength) {
	const emptyResponse = (requests) =>
		Promise.resolve({
			results: requests.map(() => ({
				hits: [],
				nbHits: 0,
				nbPages: 0,
				page: 0,
				processingTimeMS: 0,
				hitsPerPage: 0,
				exhaustiveNbHits: false,
				query: '',
				params: '',
			})),
		});

	return {
		...client,
		search(requests) {
			const hasQuery = requests.some(
				({ params }) => params && params.query && params.query.length >= minQueryLength
			);

			if (!hasQuery) {
				return emptyResponse(requests);
			}

			return client.search(requests);
		},
	};
}

export default class StuurluiAlgolia {
	constructor(appId, apiKey, indexName, replicaIndexNames = [], options = {}) {
		this.indexName = indexName;
		const { minQueryLength = 0 } = options;
		const replicas = Array.isArray(replicaIndexNames) ? replicaIndexNames : [];
		this._client = liteClient(appId, apiKey);
		// Only skip empty/short queries for the InstantSearch client so we don't
		// fire a request to Algolia on every page load (e.g. the site-wide search page).
		const searchClient =
			minQueryLength > 0 ? createConditionalSearchClient(this._client, minQueryLength) : this._client;
		this._instantsearch = instantsearch({
			indexName: indexName,
			searchClient: searchClient,
			attributesForFaceting: ['event-date'],
			future: {
				preserveSharedStateOnUnmount: true,
			},
			routing: {
				router: history({
					createURL({ location, routeState, qsModule }) {
						const { origin, pathname } = location;
						const params = {};

						const facets = [...strl_vars.algolia_filters];

						facets.forEach((facet) => {
							if (routeState[facet] && routeState[facet].length) {
								// For hierarchical facets, we need to preserve the hierarchy structure
								if (Array.isArray(routeState[facet])) {
									params[facet] = routeState[facet].join(',');
								} else {
									params[facet] = routeState[facet];
								}
							}
						});

						params.q = routeState.query || undefined;

						const queryString = qsModule.stringify(params, {
							addQueryPrefix: true,
							arrayFormat: 'repeat',
						});

						return `${origin}${pathname}${queryString}`;
					},
					parseURL({ qsModule, location }) {
						const parsed = qsModule.parse(location.search.slice(1), {
							ignoreQueryPrefix: true,
							comma: false,
						});

						const facets = [...strl_vars.algolia_filters];

						const result = facets.reduce((acc, facet) => {
							if (parsed[facet]) {
								// If the facet is hierarchical, ensure it's handled correctly
								acc[facet] = Array.isArray(parsed[facet]) ? parsed[facet] : parsed[facet].split(',');
							} else {
								acc[facet] = undefined;
							}
							return acc;
						}, {});

						// Add query field separately
						result.query = parsed.q || undefined;

						return result;
					},
				}),
				stateMapping: {
					stateToRoute(uiState) {
						const s =
							uiState[indexName] ||
							replicas
								.map((r) => uiState[r])
								.find(
									(x) => x && (x.query || (x.refinementList && Object.keys(x.refinementList).length))
								) ||
							{};
						const facets = strl_vars.algolia_filters;
						let results = {
							query: s.query || '',
						};

						facets.forEach((facet) => {
							const key = facet;
							results[key] = s.refinementList?.[mapFacetToRefinement[key] || ''] || [];
						});

						return results;
					},

					routeToState(routeState) {
						const facets = strl_vars.algolia_filters;
						let results = {
							hierarchicalMenu: {},
							refinementList: {},
							query: routeState.query || '',
						};

						facets.forEach((facet) => {
							const key = facet;
							if (routeState[key]) {
								const refinementKey = mapFacetToRefinement[key];
								if (refinementKey) {
									results.refinementList[refinementKey] = routeState[key] || [];
								}
							}
						});

						const state = { [indexName]: results };
						replicas.forEach((name) => {
							state[name] = results;
						});
						return state;
					},
				},
			},
		});
	}

	start() {
		this._instantsearch.start();
	}

	addWidgets(widgets) {
		this._instantsearch.addWidgets(widgets);
	}
}
